import { Badge } from "../../../../../shared/components/badge/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../shared/components/dropdown-menu/DropdownMenu";
import { Button } from "../../../../../shared/components/button/Button";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { DataTable } from "../../../../../shared/components/data-table/DataTable";
import { useNavigate } from "react-router";
import { resolveObjectivesTrackerDetailRoute } from "../../../../../shared/lib/routesResolver";
import { ArrowUpDown, FileInput } from "lucide-react";
import { Link } from "react-router-dom";
import { formatToEurosCurrency } from "../../../../../shared/lib/utils";
import { useTranslation } from "react-i18next";
import DropdownMenuItemDeletion from "../../../../../shared/components/dropdown-menu/DropdownMenuItemDeletion";
import DropdownMenuItemEdition from "../../../../../shared/components/dropdown-menu/DropdownMenuItemEdition";

const ObjectivesTrackerOverviewTable = ({ data, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns = [
    {
      accessorKey: "client",
      sortingFn: (rowA, rowB, columnId) => {
        const aName = rowA.original.client.name;
        const bName = rowB.original.client.name;

        return aName < bName ? 1 : aName > bName ? -1 : 0;
      },
      header: ({ column }) => {
        return (
          <div className="hidden w-[40px] sm:table-cell">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("objectivesTracker.overview.table.header.client")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const client = row.original.client;
        return (
          <div className="hidden sm:table-cell">
            <div className="flex flex-row">
              <img
                alt="logo cliente"
                className="aspect-square rounded-md object-fit"
                height={64}
                src={client.logoPath}
                width={64}
              />
              <div className="w-[20px]"></div>
              <div className="flex items-center font-medium">
                {client.name} - ({client.code})
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("objectivesTracker.overview.table.header.name")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="font-medium">
            <Link
              to={resolveObjectivesTrackerDetailRoute(data.id)}
              className="underline"
              state={{ id: data.id }}
            >
              {row.getValue("name")}
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "beginDate",
      header: () => (
        <div className="hidden md:table-cell">
          {t("objectivesTracker.overview.table.header.beginDate")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden md:table-cell">{row.getValue("beginDate")}</div>
      ),
    },
    {
      accessorKey: "endDate",
      header: () => (
        <div className="hidden md:table-cell">
          {t("objectivesTracker.overview.table.header.endDate")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden md:table-cell">{row.getValue("endDate")}</div>
      ),
    },
    {
      accessorKey: "enabled",
      header: t("objectivesTracker.overview.table.header.status"),
      cell: ({ row }) => (
        <Badge variant={row.getValue("enabled") ? "outline" : "secondary"}>
          {row.getValue("enabled")
            ? t("defaults.activeLabel")
            : t("defaults.inactiveLabel")}
        </Badge>
      ),
    },
    {
      accessorKey: "createdBy",
      header: t("objectivesTracker.overview.table.header.createdBy"),
    },
    {
      accessorKey: "lastEditDate",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("objectivesTracker.overview.table.header.lastEditDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
    },
    {
      header: t("objectivesTracker.overview.table.header.difference"),
      cell: ({ row }) => {
        const data = row.original;
        const difference =
          data.result === null
            ? "N/A"
            : formatToEurosCurrency(data.result.goal - data.result.achieved);

        return <div>{difference}</div>;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const data = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-haspopup="true" size="icon" variant="ghost">
                <DotsHorizontalIcon className="h-4 w-4" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="hover:cursor-pointer flex flex-row items-center gap-x-2"
                onClick={() =>
                  navigate(resolveObjectivesTrackerDetailRoute(data.id), {
                    state: { id: data.id },
                  })
                }
              >
                <FileInput className="h-4 w-4" />
                {t("defaults.goToDetailLabel")}
              </DropdownMenuItem>
              <DropdownMenuItemEdition onEditHandler={() => onEdit(data)} />
              <DropdownMenuItemDeletion
                onDeleteHandler={() => onDelete(data.id)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return <DataTable columns={columns} data={data} />;
};

export default ObjectivesTrackerOverviewTable;
